import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Box, Skeleton, Typography } from '@mui/material';

import { TranslationKeys } from 'app/translations';

import type { EmployeeEmploymentContract, UseQueryRefetch } from 'app/types';

import { useDialog, useSnakeBar, useEmploymentContractTypeSelectItems } from 'app/hooks';
import { useEmployeeDeleteContractMutation } from 'app/hooks/api/employee/useEmployeeDeleteContractMutation';

import { getDisplayDateValue, getDisplayOptionalValue, getDisplayUTCDateValue } from 'app/utils';

import ActionButton from 'app/components/ActionButton';
import DetailsPanel from 'app/components/DetailsPanel';
import DetailsPanelRow from 'app/components/DetailsPanelRow';

import { EmployeeDetailsPanelHeaderWithSyncToggle } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeDetailsPanelHeaderWithSyncToggle';

import { DivPanelBodyDetailsStyled } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeEmploymentTab/EmployeeEmploymentTab.styles';

import { EmployeeEmploymentContractFormDialog } from './components';
import { EmployeeEmploymentContractFields } from './EmployeeEmploymentContracts.types';

import { CONNECTION, EMPLOYEE } from 'app/common/Authorization/entities';
import { DELETE, EDIT, RUN } from 'app/common/Authorization/permissions';
import { ConfirmDialog, HasAccessTo } from 'app/components';

type EmployeeEmploymentContractsProps = {
  loading: boolean;
  isRegularConnection: boolean;
  refetchGetEmploymentsQuery: UseQueryRefetch;
  connectionId: string | undefined;
  employeeId: string | undefined;
  employmentId: string | undefined;
  contracts: Array<EmployeeEmploymentContract> | undefined;
  synchronizationStatus: boolean | undefined;
  refetchGetEmployeeSynchronizationQuery: UseQueryRefetch;
  disabledActionButtonsWhileLoading: boolean;
};

export const EmployeeEmploymentContracts = ({
  connectionId,
  employeeId,
  employmentId,
  contracts = [],
  loading,
  isRegularConnection,
  refetchGetEmploymentsQuery,
  synchronizationStatus,
  refetchGetEmployeeSynchronizationQuery,
  disabledActionButtonsWhileLoading,
}: EmployeeEmploymentContractsProps) => {
  const { employerId } = useParams();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const intl = useIntl();
  const { getEmploymentContractTypeDisplayNameByValue } = useEmploymentContractTypeSelectItems();

  const {
    dialogState: contractFormDialogState,
    openDialog: openContractFormDialog,
    closeDialog: closeContractFormDialog,
  } = useDialog();

  const {
    dialogState: deleteContractDialog,
    openDialog: openConfirmDeleteDialog,
    closeDialog: closeConfirmDeleteDialog,
  } = useDialog();

  const { mutate: deleteEmployeeContract } = useEmployeeDeleteContractMutation();

  const [expanded, setExpanded] = React.useState(contracts.map(() => false));
  const [contractToEdit, setContractToEdit] = React.useState<EmployeeEmploymentContract | null>(null);
  const [contractToDelete, setContractToDelete] = React.useState<EmployeeEmploymentContract | null>(null);
  const hasContracts = contracts.length > 0;
  const hasActiveSynchronization = synchronizationStatus !== undefined ? synchronizationStatus : false;
  const disabledIfNoAccessToEmployeeEdit = !HasAccessTo(EMPLOYEE, EDIT);
  const disabledIfNoAccessToConnectionDelete = !HasAccessTo(CONNECTION, DELETE);
  const disabledIfNoAccessToConnectionRun = !HasAccessTo(CONNECTION, RUN);

  const shouldRenderEmployeeEmploymentContractFormDialog =
    !!employerId && !!connectionId && !!employeeId && !!employmentId && contractFormDialogState;

  const toggleContractDetailsPanel = React.useCallback(
    ({ id, expanded: newExpanded }) => {
      const newState = new Array(expanded.length).fill(false);
      newState[id] = !newExpanded;
      setExpanded(newState);
    },
    [expanded],
  );

  const onOpenContractFormDialog = React.useCallback(
    (contract?: EmployeeEmploymentContract) => {
      setContractToEdit(contract || null);
      openContractFormDialog();
    },
    [openContractFormDialog],
  );

  const onCloseContractFormDialog = React.useCallback(() => {
    setContractToEdit(null);
    closeContractFormDialog();
  }, [closeContractFormDialog]);

  const onClickAddButton = React.useCallback(() => {
    onOpenContractFormDialog();
  }, [onOpenContractFormDialog]);

  const onClickEditButton = React.useCallback(
    (contract: EmployeeEmploymentContract) => () => {
      onOpenContractFormDialog(contract);
    },
    [onOpenContractFormDialog],
  );

  const onClickDeleteButton = React.useCallback(
    (contract: EmployeeEmploymentContract) => () => {
      setContractToDelete(contract);
      openConfirmDeleteDialog();
    },
    [openConfirmDeleteDialog],
  );

  const onCloseDeleteContract = React.useCallback(() => {
    setContractToDelete(null);
    closeConfirmDeleteDialog();
  }, [closeConfirmDeleteDialog]);

  const onConfirmDeleteContract = React.useCallback(() => {
    if (!contractToDelete || !connectionId || !employeeId) {
      return;
    }

    const method = 'deleteEmployeeContract';

    deleteEmployeeContract(
      {
        connectionId: connectionId,
        employeeId: employeeId,
        contractId: contractToDelete.mongoId,
      },
      {
        onSuccess: () => {
          refetchGetEmploymentsQuery();
          showSuccessSnakeBar({
            method,
            message: intl.formatMessage({ id: TranslationKeys.global_update_successMessage }),
          });
          onCloseDeleteContract();
        },
        onError: () => {
          showErrorSnakeBar({
            method,
            message: intl.formatMessage({ id: TranslationKeys.global_update_errorMessage }),
          });
        },
      },
    );
  }, [
    connectionId,
    employeeId,
    contractToDelete,
    refetchGetEmploymentsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    deleteEmployeeContract,
    onCloseDeleteContract,
    intl,
  ]);

  const LoadingDetailsBody = React.useMemo(() => {
    return (
      <>
        {new Array(5).fill(null).map((_, idx) => (
          <Skeleton key={idx} height={24} />
        ))}
      </>
    );
  }, []);

  const NoContractsDetailsBody = React.useMemo(() => {
    return (
      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body2">
          <FormattedMessage id={TranslationKeys.employees_detail_contracts_noContracts} />
        </Typography>
      </Box>
    );
  }, []);

  const ContractsDetailsBody = React.useMemo(() => {
    return (contracts || []).map((contract, idx) => {
      const expandedIfOnlyOneContract = contracts.length === 1;
      const isExpanded = typeof expanded[idx] !== 'undefined' ? expanded[idx] : false;

      const rows: Record<string, any> = {
        [EmployeeEmploymentContractFields.StartDate.Label]: getDisplayDateValue(contract?.startDate),
        [EmployeeEmploymentContractFields.EndDate.Label]: getDisplayUTCDateValue(contract?.endDate),
        [EmployeeEmploymentContractFields.ContractType.Label]: contract?.contractType
          ? getEmploymentContractTypeDisplayNameByValue(contract.contractType)
          : getDisplayOptionalValue(contract?.contractType),
        [EmployeeEmploymentContractFields.ContractId.Label]: getDisplayOptionalValue(contract?.contractId),
        [EmployeeEmploymentContractFields.CreationDate.Label]: getDisplayDateValue(contract?.creationDate),
        [EmployeeEmploymentContractFields.EmploymentSequence.Label]: getDisplayOptionalValue(
          contract?.employmentSequence,
        ),
      };
      const startDate = contract.startDate.split('T')[0];
      const endDate = contract.endDate ? contract.endDate.split('T')[0] : '';

      return (
        <div key={idx}>
          <DetailsPanelRow
            title={<Typography variant="h6">{`${startDate} - ${endDate}`}</Typography>}
            expanded={expandedIfOnlyOneContract || isExpanded}
            rows={rows}
            loading={loading}
            id={idx}
            onExpand={toggleContractDetailsPanel}
            onDeleteButtonClick={isRegularConnection ? onClickDeleteButton(contract) : undefined}
            onEditButtonClick={isRegularConnection ? onClickEditButton(contract) : undefined}
            disabledEditButton={
              disabledActionButtonsWhileLoading || hasActiveSynchronization || disabledIfNoAccessToEmployeeEdit
            }
            disabledDeleteButton={
              disabledActionButtonsWhileLoading ||
              hasActiveSynchronization ||
              disabledIfNoAccessToConnectionDelete ||
              disabledIfNoAccessToEmployeeEdit
            }
          />
        </div>
      );
    });
  }, [
    contracts,
    expanded,
    loading,
    toggleContractDetailsPanel,
    isRegularConnection,
    onClickEditButton,
    onClickDeleteButton,
    disabledActionButtonsWhileLoading,
    hasActiveSynchronization,
    disabledIfNoAccessToEmployeeEdit,
    disabledIfNoAccessToConnectionDelete,
    getEmploymentContractTypeDisplayNameByValue,
  ]);

  const DetailsPanelBody = React.useMemo(() => {
    const finishedLoadingAndHasContracts = !loading && hasContracts;
    const finishedLoadingAndHasNoContracts = !loading && !hasContracts;

    return (
      <DivPanelBodyDetailsStyled isRegularConnection={isRegularConnection}>
        <>
          {loading && LoadingDetailsBody}
          {finishedLoadingAndHasContracts && ContractsDetailsBody}
          {finishedLoadingAndHasNoContracts && NoContractsDetailsBody}
        </>
      </DivPanelBodyDetailsStyled>
    );
  }, [loading, hasContracts, isRegularConnection, LoadingDetailsBody, ContractsDetailsBody, NoContractsDetailsBody]);

  const DetailsPanelActions = React.useMemo(() => {
    return (
      <ActionButton
        messageId={TranslationKeys.button_employmentContracts_add}
        variant="text"
        icon={faPlus}
        onClick={onClickAddButton}
        disabled={
          disabledActionButtonsWhileLoading ||
          hasActiveSynchronization ||
          disabledIfNoAccessToEmployeeEdit ||
          disabledIfNoAccessToConnectionRun
        }
      />
    );
  }, [
    disabledActionButtonsWhileLoading,
    disabledIfNoAccessToEmployeeEdit,
    hasActiveSynchronization,
    onClickAddButton,
    disabledIfNoAccessToConnectionRun,
  ]);

  const DetailsPanelHeader = React.useMemo(() => {
    return (
      <EmployeeDetailsPanelHeaderWithSyncToggle
        field={'contract'}
        loading={loading}
        synchronizationStatus={synchronizationStatus}
        refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
        disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
        messageId={TranslationKeys.employees_detail_contracts}
        employeeId={employeeId}
        connectionId={connectionId}
      />
    );
  }, [
    loading,
    synchronizationStatus,
    refetchGetEmployeeSynchronizationQuery,
    disabledActionButtonsWhileLoading,
    employeeId,
    connectionId,
  ]);

  return (
    <>
      {shouldRenderEmployeeEmploymentContractFormDialog && (
        <EmployeeEmploymentContractFormDialog
          open={contractFormDialogState}
          closeDialog={onCloseContractFormDialog}
          connectionId={connectionId}
          employeeId={employeeId}
          employmentId={employmentId}
          contract={contractToEdit}
          refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
        />
      )}

      {deleteContractDialog && (
        <ConfirmDialog
          title={<FormattedMessage id={TranslationKeys.employees_detail_contracts_confirmDeleteTitle} />}
          open={deleteContractDialog}
          onConfirm={onConfirmDeleteContract}
          onClose={onCloseDeleteContract}
        >
          <FormattedMessage id={TranslationKeys.employees_detail_contracts_confirmDeleteMessage} />
        </ConfirmDialog>
      )}

      <DetailsPanel
        header={DetailsPanelHeader}
        body={DetailsPanelBody}
        actions={isRegularConnection ? DetailsPanelActions : undefined}
        scrollBody={true}
      />
    </>
  );
};
